import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
 // useMediaQuery,
 // useTheme,
  Divider,
} from '@mui/material';
import { GetPrestaciones } from '../../logic/aplication/getPrestaciones';
import { ProtocoloService } from '../../logic/domain/services/protocoloService';
import { ApiImplementation } from '../../logic/infraestructure/api/apiImplementation';
import { Prestacion } from '../../logic/domain/entities/prestacion';
import { GetPrestacionPreparacion } from '../../logic/aplication/getPrestacionPreparacion';
import { debounce } from 'lodash';
import pdfMake from 'pdfmake/build/pdfmake';
// @ts-ignore
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { GetPrestacionReferencia } from '../../logic/aplication/getPrestacionReferencia';
import { PrestacionReferencia } from '../../logic/domain/entities/prestacionReferencia';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
// import { useNavigate } from 'react-router-dom';

// Configurar las fuentes de pdfMake
// @ts-ignore
if (pdfMake.vfs === undefined && pdfFonts.pdfMake) {
  // @ts-ignore
  pdfMake.vfs = pdfFonts.pdfMake.vfs;
}

interface DashboardProps {
  onLogoutListadoPrestaciones: () => void;
}

const ListadoPrestaciones: React.FC<DashboardProps> = ({onLogoutListadoPrestaciones}) => {
  const [prestaciones, setPrestaciones] = useState<Prestacion[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredPrestaciones, setFilteredPrestaciones] = useState<Prestacion[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchInitiated, setSearchInitiated] = useState<boolean>(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>('');
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [referencias, setReferencias] = useState<Record<number, PrestacionReferencia[]>>({});

  const apiImplementation = useMemo(() => new ApiImplementation(), []);
  const protocoloService = useMemo(() => new ProtocoloService(apiImplementation), [apiImplementation]);
  const getPrestaciones = useMemo(() => new GetPrestaciones(protocoloService), [protocoloService]);
  const getPrestacionPreparacion = useMemo(() => new GetPrestacionPreparacion(protocoloService), [protocoloService]);
  const getPrestacionReferencia = useMemo(() => new GetPrestacionReferencia(protocoloService), [protocoloService]);

  //const theme = useTheme();
  // const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchPrestaciones = async () => {
      try {
        const storedPrestaciones = localStorage.getItem('prestaciones');
        if (storedPrestaciones) {
          const prestacionesData = JSON.parse(storedPrestaciones);
          const prestacionesInstancias = Prestacion.fromCollection(prestacionesData);
          setPrestaciones(prestacionesInstancias);
        } else {
          const token = localStorage.getItem('token') as string;
          if (token) {
            const prestacionesData = await getPrestaciones.execute(token);
            const sortedPrestaciones = prestacionesData.sort((a, b) =>
              a.getPrestacionDescripcion().localeCompare(b.getPrestacionDescripcion())
            );
            const prestacionesInstancias = Prestacion.fromCollection(sortedPrestaciones);
            localStorage.setItem('prestaciones', JSON.stringify(prestacionesInstancias));
            setPrestaciones(prestacionesInstancias);
          } else {
            console.error('Token no encontrado en localStorage');
          }
        }
      } catch (error) {
        console.error('Error al obtener prestaciones:', error);
      }
    };

    fetchPrestaciones();
  }, [getPrestaciones]);

  const handleSearch = useMemo(
    () =>
      debounce((term: string) => {
        setLoading(true);
        setSearchTerm(term);
      }, 300),
    []
  );

  useEffect(() => {
    if (searchTerm) {
      setLoading(true);
      const filtered = prestaciones.filter((prestacion) =>
        prestacion.getPrestacionDescripcion().toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPrestaciones(filtered);
      setLoading(false);
      setSearchInitiated(true);
    } else {
      setFilteredPrestaciones([]);
      setSearchInitiated(false);
      setLoading(false);
    }
  }, [searchTerm, prestaciones]);

  const toggleCard = async (index: number, prestacionId: number) => {
    setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));

    // Si no hemos cargado las referencias antes, las obtenemos
    if (!referencias[prestacionId]) {
      const token = localStorage.getItem('token') as string;
      if (token) {
        try {
          const refData = await getPrestacionReferencia.execute(prestacionId, token);
          setReferencias((prevState) => ({
            ...prevState,
            [prestacionId]: refData,
          }));
        } catch (error) {
          console.error('Error al obtener referencias:', error);
        }
      }
    }
  };

  const handleOpenModal = async (prestacionId: number) => {
    const token = localStorage.getItem('token') as string;
    if (token) {
      try {
        const htmlContent = await getPrestacionPreparacion.execute(prestacionId, token);
        setModalContent(htmlContent[0]?.getPreparacion() || 'Contenido no disponible');
        setOpenModal(true);
      } catch (error) {
        console.error(error);
        setErrorMessage('Sesión expirada');
        setErrorModalOpen(true);
      }
    }else{
      setErrorMessage('Sesión expirada');
      setErrorModalOpen(true);
    }
  };

  const handleDownloadPDF = () => {
    const extractTextFromHTML = (html: string) => {
      const tempElement = document.createElement('div');
      tempElement.innerHTML = html;
      return tempElement.innerText; // Solo devuelve el texto sin HTML
    };

    const contentText = extractTextFromHTML(modalContent); // Extraer solo texto del contenido

    const docDefinition = {
      content: [
          { text: 'Preparación', style: 'header' },
          { text: contentText, style: 'content' }
      ],
      styles: {
          header: {
              fontSize: 18,
              bold: true,
              margin: [0, 20, 0, 10] as [number, number, number, number], // 4 elementos: [izquierda, arriba, derecha, abajo]
          },
          content: {
              fontSize: 12,
              margin: [0, 10] as [number, number], // 2 elementos: [horizontal, vertical]
          },
      },
  } as TDocumentDefinitions; // Asegúrate de que el tipo coincida con TDocumentDefinitions
  
  

    // Generar el PDF
    pdfMake.createPdf(docDefinition).download('Preparacion.pdf');
  };
  return (
    <div style={{ padding: '0 16px' }}>
      <Grid container justifyContent="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Buscar prestación"
            variant="outlined"
            fullWidth
            margin="normal"
            onChange={(e) => handleSearch(e.target.value)}
            sx={{
              backgroundColor: 'white',
              width: '100%',
              borderRadius: '8px',
            }}
          />
        </Grid>
      </Grid>

      {loading && (
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      )}

      {!loading && !searchInitiated && filteredPrestaciones.length === 0 && (
        <Typography sx={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px' }}>
          {process.env.REACT_APP_MENSAJE_FILTRO_PRESTACIONES}
        </Typography>
      )}

      {!loading && searchInitiated && filteredPrestaciones.length === 0 && (
        <Typography sx={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px' }}>
          {process.env.REACT_APP_MENSAJE_FILTRO_PRESTACIONES_NO_ENCONTRADO}
        </Typography>
      )}

      {!loading && filteredPrestaciones.length > 0 && (
        <div>
          {filteredPrestaciones.map((prestacion, index) => {
            const prestacionReferencias = referencias[prestacion.getPrestacionCodigo()] || [];
            const isExpanded = expandedIndex === index;

            return (
              <Card key={prestacion.getPrestacionCodigo()} sx={{ mb: 2, borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
    <Typography 
      variant="h6" 
      sx={{ 
        fontWeight: 'bold', 
        color: '#1976d2', 
        mb: 1 
      }}
    >
      {prestacion.getPrestacionDescripcion() || 'Descripción no disponible'}
    </Typography>

    {isExpanded && (
      <>
        <Typography 
          sx={{ 
            fontStyle: 'italic', 
            color: '#616161', 
            mb: 2 
          }}
        >
          {prestacion.getPreparaciones() || 'Preparación no disponible'}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Descripción de la Prestación:
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {prestacion.getPrestacionDescripcion()}
        </Typography>

        {prestacionReferencias.length > 0 ? (
          <div>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Referencias:
            </Typography>
            {prestacionReferencias.map((ref: PrestacionReferencia, idx) => (
              <div key={idx} style={{ marginBottom: '15px' }}>
                <Typography variant="body2" sx={{ color: '#424242', mb: 1 }}>
                  <strong>Determinación:</strong> {ref.getDescripcionDeterminacion()}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ color: '#424242', mb: 1 }}
                >
                  <strong>Valores de Referencia:</strong>
                  <span dangerouslySetInnerHTML={{ __html: ref.getValoresReferencia() || 'No disponible' }} />
                </Typography>
                <Typography variant="body2" sx={{ color: '#424242', mb: 1 }}>
                  <strong>Material:</strong> {ref.getMaterial()}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </div>
            ))}
          </div>
        ) : (
          <Typography variant="body2" sx={{ color: '#757575' }}>
            No hay referencias disponibles.
          </Typography>
        )}
      </>
    )}
  </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    onClick={() => toggleCard(index, prestacion.getPrestacionCodigo())}
                    color={isExpanded ? 'secondary' : 'primary'}
                  >
                    {isExpanded ? 'Ocultar' : 'Mostrar'}
                  </Button>
                  <Button size="small" onClick={() => handleOpenModal(prestacion.getPrestacionCodigo())}>
                    Ver Preparación
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth>
        <DialogTitle>Preparación</DialogTitle>
        <DialogContent>
          <div id="pdf-content" dangerouslySetInnerHTML={{ __html: modalContent }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadPDF}>Descargar PDF</Button>
          <Button onClick={() => setOpenModal(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onLogoutListadoPrestaciones}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ListadoPrestaciones;